import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import { CheckCircle, ArrowBack } from '@mui/icons-material';

export const SubscriptionSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);

  useEffect(() => {
    const sessionIdParam = searchParams.get('session_id');
    
    if (!sessionIdParam) {
      setError('No session ID provided');
      setLoading(false);
      return;
    }

    setSessionId(sessionIdParam);
    
    // Verify the session with your backend
    // TODO: Implement session verification
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  }, [searchParams]);

  const handleBackToApp = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          p: 3,
        }}
      >
        <CircularProgress size={60} sx={{ mb: 3 }} />
        <Typography variant="h6" color="text.secondary">
          Verifying your payment...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          p: 3,
        }}
      >
        <Alert severity="error" sx={{ mb: 3, maxWidth: 500 }}>
          {error}
        </Alert>
        <Button
          variant="contained"
          startIcon={<ArrowBack />}
          onClick={handleBackToApp}
        >
          Back to Application
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: 'background.default',

      }}
    >
      <Card sx={{ maxWidth: 500, width: '100%' }}>
        <CardContent sx={{ textAlign: 'center', p: 4 }}>
          <CheckCircle
            sx={{
              fontSize: 80,
              color: 'success.main',
              mb: 3,
            }}
          />
          
          <Typography variant="h4" gutterBottom color="success.main">
            Payment Successful!
          </Typography>
          
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Thank you for your subscription. Your payment has been processed successfully.
          </Typography>

          {sessionId && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Session ID: {sessionId}
            </Typography>
          )}

          <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
            <Typography variant="body2">
              <strong>What's next?</strong>
              <br />
              • Your subscription is now active
              <br />
              • You can access all premium features
              <br />
              • You'll receive a confirmation email shortly
              <br />
              • Manage your subscription in the Company settings
            </Typography>
          </Alert>

          <Button
            variant="contained"
            size="large"
            startIcon={<ArrowBack />}
            onClick={handleBackToApp}
            fullWidth
          >
            Back to Application
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};
